/**
 * Job Detail Page
 * Shows job details and allows providers to bid
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useAuth, useLanguage, API } from "@/context/AppContext";
import { MapPin, Clock, Star, Check, Loader2, Trash2, CheckCircle } from "lucide-react";

const JobDetailPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [job, setJob] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [bidForm, setBidForm] = useState({ amount: '', message: '', estimated_duration: '' });
  const [submitting, setSubmitting] = useState(false);
  const { jobId } = { jobId: window.location.pathname.split('/').pop() };
  const navigate = useNavigate();

  useEffect(() => {
    loadJob();
  }, []);

  const loadJob = async () => {
    try {
      const jobRes = await axios.get(`${API}/jobs/${jobId}`, { withCredentials: true });
      setJob(jobRes.data);
      
      const bidsRes = await axios.get(`${API}/bids/job/${jobId}`, { withCredentials: true });
      setBids(bidsRes.data);
    } catch (error) {
      toast.error('Auftrag nicht gefunden');
      navigate('/dashboard/jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitBid = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/bids`, {
        job_id: jobId,
        amount: parseFloat(bidForm.amount),
        message: bidForm.message,
        estimated_duration: bidForm.estimated_duration
      }, { withCredentials: true });

      toast.success('Gebot abgegeben!');
      setBidDialogOpen(false);
      loadJob();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Abgeben');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptBid = async (bidId) => {
    try {
      await axios.put(`${API}/bids/${bidId}/accept`, {}, { withCredentials: true });
      toast.success('Gebot angenommen!');
      loadJob();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const handleCompleteJob = async () => {
    if (rating < 1 || rating > 5) {
      toast.error('Bitte geben Sie eine Bewertung (1-5 Sterne)');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(`${API}/jobs/${jobId}/complete`, {
        rating: rating,
        review: reviewText
      }, { withCredentials: true });
      toast.success('Auftrag abgeschlossen und bewertet!');
      setCompleteDialogOpen(false);
      loadJob();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteJob = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`${API}/jobs/${jobId}`, { withCredentials: true });
      toast.success('Auftrag gelöscht');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
      </div>
    );
  }

  if (!job) return null;

  const isOwner = job.client_id === user?.user_id;
  const isProvider = user?.role === 'provider';
  const hasMyBid = bids.some(b => b.provider_id === user?.user_id);

  return (
    <div className="space-y-6" data-testid="job-detail-page">
      <Button variant="ghost" onClick={() => navigate('/dashboard/jobs')} className="mb-4">
        ← {t('jobs')}
      </Button>

      <Card className="border-gray-200">
        <CardContent className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {job.title}
            </h1>
            <Badge className={`${
              job.status === 'open' ? 'bg-green-100 text-green-700' :
              job.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {job.status === 'open' && t('open')}
              {job.status === 'in_progress' && t('inProgress')}
              {job.status === 'completed' && t('completed')}
            </Badge>
          </div>
          
          {/* Client Info - Who posted the job */}
          <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-[#0044CC] text-white">
                {job.client_name?.charAt(0) || 'K'}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium">{job.client_name || 'Kunde'}</p>
              <p className="text-sm text-gray-500">Auftraggeber</p>
            </div>
          </div>
          
          <p className="text-gray-600 whitespace-pre-wrap">{job.description}</p>
          
          <div className="flex flex-wrap gap-6 mt-6 pt-6 border-t border-gray-100">
            <div>
              <p className="text-sm text-gray-500">{t('city')}</p>
              <p className="font-medium flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {job.location?.city || t('switzerland')}
              </p>
            </div>
            {job.budget_max && (
              <div>
                <p className="text-sm text-gray-500">Budget</p>
                <p className="font-bold text-[#0044CC]">
                  CHF {job.budget_min ? `${job.budget_min} - ` : ''}{job.budget_max}
                </p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-500">{t('bids')}</p>
              <p className="font-medium">{job.bids_count}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bid Button for Providers */}
      {isProvider && job.status === 'open' && !hasMyBid && (
        <Dialog open={bidDialogOpen} onOpenChange={setBidDialogOpen}>
          <DialogTrigger asChild>
            <Button className="w-full bg-[#0044CC] hover:bg-[#003399] text-white h-12 rounded-full" data-testid="place-bid-btn">
              {t('placeBid')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('placeBid')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmitBid} className="space-y-4">
              <div>
                <Label>{t('bidAmount')}</Label>
                <Input
                  type="number"
                  value={bidForm.amount}
                  onChange={(e) => setBidForm({ ...bidForm, amount: e.target.value })}
                  placeholder="500"
                  required
                  data-testid="bid-amount-input"
                />
              </div>
              <div>
                <Label>{t('bidMessage')}</Label>
                <Textarea
                  value={bidForm.message}
                  onChange={(e) => setBidForm({ ...bidForm, message: e.target.value })}
                  placeholder={t('bidMessagePlaceholder')}
                  required
                  data-testid="bid-message-input"
                />
              </div>
              <div>
                <Label>{t('estimatedDuration')}</Label>
                <Input
                  value={bidForm.estimated_duration}
                  onChange={(e) => setBidForm({ ...bidForm, estimated_duration: e.target.value })}
                  placeholder={t('durationPlaceholder')}
                  data-testid="bid-duration-input"
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-[#0044CC] hover:bg-[#003399] text-white rounded-full" data-testid="submit-bid-btn">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : t('submitBid')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Client Actions - Complete or Delete Job */}
      {isOwner && (
        <div className="flex gap-3">
          {/* Complete Job Button - Only for in_progress jobs */}
          {job.status === 'in_progress' && (
            <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
              <DialogTrigger asChild>
                <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white h-12 rounded-full" data-testid="complete-job-btn">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Auftrag abschliessen
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Auftrag abschliessen & bewerten</DialogTitle>
                  <DialogDescription>
                    Bitte bewerten Sie die Arbeit des Dienstleisters
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Bewertung *</Label>
                    <div className="flex gap-2 mt-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          className="p-1 hover:scale-110 transition-transform"
                        >
                          <Star
                            className={`w-8 h-8 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                          />
                        </button>
                      ))}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{rating} von 5 Sternen</p>
                  </div>
                  <div>
                    <Label>Bewertungstext (optional)</Label>
                    <Textarea
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder="Wie war Ihre Erfahrung mit dem Dienstleister?"
                      className="mt-1"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCompleteDialogOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button 
                    onClick={handleCompleteJob} 
                    disabled={submitting}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Abschliessen & Bewerten'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* Delete Job Button - Only for open jobs */}
          {job.status === 'open' && (
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50 h-12 rounded-full" data-testid="delete-job-btn">
                  <Trash2 className="w-5 h-5 mr-2" />
                  Löschen
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Auftrag löschen?</DialogTitle>
                  <DialogDescription>
                    Sind Sie sicher, dass Sie diesen Auftrag löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button 
                    onClick={handleDeleteJob} 
                    disabled={submitting}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Löschen'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      )}

      {/* Bids List */}
      {(isOwner || hasMyBid) && bids.length > 0 && (
        <div>
          <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {isOwner ? `${t('bids')} (${bids.length})` : t('myBids')}
          </h2>
          <div className="space-y-3">
            {bids.map((bid) => (
              <Card key={bid.bid_id} className="border-gray-200">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-[#0044CC] text-white">
                            {bid.provider_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold">{bid.provider_name}</p>
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                            <span className="text-sm text-gray-500">{bid.provider_rating || '0.0'}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-600 mt-3 text-sm">{bid.message}</p>
                      {bid.estimated_duration && (
                        <p className="text-xs text-gray-400 mt-2">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {bid.estimated_duration}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-[#0044CC]">CHF {bid.amount}</p>
                      {bid.status === 'pending' && isOwner && job.status === 'open' && (
                        <Button
                          onClick={() => handleAcceptBid(bid.bid_id)}
                          className="mt-2 bg-green-600 hover:bg-green-700 text-white rounded-full"
                          size="sm"
                          data-testid={`accept-bid-${bid.bid_id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          {t('accept')}
                        </Button>
                      )}
                      {bid.status === 'accepted' && (
                        <Badge className="mt-2 bg-green-100 text-green-700">{t('accepted')}</Badge>
                      )}
                      {bid.status === 'rejected' && (
                        <Badge className="mt-2 bg-red-100 text-red-700">{t('rejected')}</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetailPage;
